import { FC, useEffect, useMemo, useRef, useState } from 'react';

import Tabs from '@shared/components/Tabs';
import { MiniMarkdown } from '@shared/releaseNotes/MiniMarkdown';
import {
  ReleaseNoteChannel,
  ReleaseNoteEntry,
  RELEASE_NOTE_CHANNEL_LABEL,
  RELEASE_NOTE_CHANNEL_ORDER,
} from '@shared/releaseNotes/releaseNotes.interface';
import {
  releaseNoteChannelsOfRole,
  useReleaseNotes,
} from '@shared/releaseNotes/useReleaseNotes';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Button, Input, Table, TableColumnsType, Tag } from 'antd';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

/**
 * 배포마다 남기는 채널별 패치노트를 앱 안에서 읽는 페이지.
 * 원본은 docs/releases/notes/*.md이고 scripts/genReleaseNotes.js로 번들에 심는다.
 *
 * 목록은 **배포일 한 줄**이고(대상은 태그로 나열), 눌러 들어가면 대상별 탭으로 본다.
 *
 * 지금은 Admin에게만 라우트가 열려 있다(전 채널 열람). 다른 role로 넓힐 때는
 * 각 콘솔에 라우트를 추가하기만 하면 되고, 보이는 채널은 role이 정한다.
 * 다만 번들은 모든 채널을 담으므로 노트 본문은 "반쯤 공개" 전제로 쓴다
 * (민감한 내용은 릴리즈 로그의 internal 항목에만 — docs/releases/README.md).
 */

interface ReleaseRow {
  releaseId: string;
  channels: ReleaseNoteChannel[];
  /** 대상별 노트의 중요 포인트를 합친 것 (중복 제거) */
  points: string[];
  notes: ReleaseNoteEntry[];
}

const MAX_POINT_TAGS = 6;

const AdminReleaseNotePage: FC = () => {
  useAdminTitleHook('업데이트 소식');

  const { roles } = useAuth();
  const channels = useMemo(() => releaseNoteChannelsOfRole(roles), [roles]);

  const { releaseIds, notesByRelease, isUnread, markAllSeen } = useReleaseNotes(channels);

  //* 페이지에 들어온 순간 "읽음" 처리하되, 어느 배포가 새 것이었는지는
  //* 화면에 남겨야 하므로 진입 시점 상태를 얼려 둔다.
  const unreadAtMountRef = useRef<Set<string> | null>(null);
  if (unreadAtMountRef.current === null) {
    unreadAtMountRef.current = new Set(releaseIds.filter(isUnread));
  }
  const unreadAtMount = unreadAtMountRef.current;

  useEffect(() => {
    markAllSeen();
  }, [markAllSeen]);

  const [keyword, setKeyword] = useState<string>('');
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
  const [channelIndex, setChannelIndex] = useState<number>(0);

  //* 배포일 한 줄 = 그날 나간 대상별 노트 묶음
  const releaseRows = useMemo<ReleaseRow[]>(() => {
    return releaseIds.map((releaseId) => {
      const notes = [...(notesByRelease[releaseId] ?? [])].sort(
        (a, b) =>
          RELEASE_NOTE_CHANNEL_ORDER.indexOf(a.channel as ReleaseNoteChannel) -
          RELEASE_NOTE_CHANNEL_ORDER.indexOf(b.channel as ReleaseNoteChannel),
      );
      const points: string[] = [];
      notes.forEach((note) =>
        note.points.forEach((point) => {
          if (!points.includes(point)) points.push(point);
        }),
      );
      return {
        releaseId,
        channels: notes.map((note) => note.channel as ReleaseNoteChannel),
        points,
        notes,
      };
    });
  }, [releaseIds, notesByRelease]);

  //* 검색은 그 배포의 모든 대상 노트를 훑어서, 하나라도 걸리면 그 배포를 남긴다
  const rows = useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) return releaseRows;
    return releaseRows.filter((row) => {
      const haystack = [
        row.releaseId,
        ...row.channels.map((channel) => RELEASE_NOTE_CHANNEL_LABEL[channel]),
        ...row.notes.map((note) =>
          [note.title, note.summary, note.points.join(' '), note.body].join('\n'),
        ),
      ]
        .join('\n')
        .toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [releaseRows, keyword]);

  const selectedRow = useMemo(
    () => releaseRows.find((row) => row.releaseId === selectedReleaseId) ?? null,
    [releaseRows, selectedReleaseId],
  );

  const channelFilters = useMemo(() => {
    const present = new Set(releaseRows.flatMap((row) => row.channels));
    return RELEASE_NOTE_CHANNEL_ORDER.filter((channel) => present.has(channel)).map(
      (channel) => ({ text: RELEASE_NOTE_CHANNEL_LABEL[channel], value: channel }),
    );
  }, [releaseRows]);

  const columns: TableColumnsType<ReleaseRow> = useMemo(
    () => [
      {
        width: 150,
        title: '배포일',
        dataIndex: 'releaseId',
        key: 'releaseId',
        align: 'center',
        render: (releaseId: string) => (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs font-bold">{releaseId}</span>
            {unreadAtMount.has(releaseId) && <Tag color="red">NEW</Tag>}
          </div>
        ),
      },
      {
        width: 260,
        title: '대상',
        dataIndex: 'channels',
        key: 'channels',
        align: 'center',
        filters: channelFilters,
        onFilter: (value, record) =>
          record.channels.includes(value as ReleaseNoteChannel),
        render: (rowChannels: ReleaseNoteChannel[]) => (
          <div className="flex flex-wrap justify-center gap-1">
            {rowChannels.map((channel) => (
              <Tag key={channel}>{RELEASE_NOTE_CHANNEL_LABEL[channel] ?? channel}</Tag>
            ))}
          </div>
        ),
      },
      {
        title: '주요 업데이트',
        dataIndex: 'points',
        key: 'points',
        render: (points: string[]) => (
          <div className="flex flex-wrap gap-1">
            {points.slice(0, MAX_POINT_TAGS).map((point) => (
              <Tag key={point} color="blue">
                {point}
              </Tag>
            ))}
            {points.length > MAX_POINT_TAGS && (
              <span className="text-xs theme-text-8">
                외 {points.length - MAX_POINT_TAGS}건
              </span>
            )}
          </div>
        ),
      },
      {
        width: 90,
        title: '',
        key: 'action',
        align: 'center',
        render: () => (
          <span className="text-xs font-bold underline theme-text-main">열기</span>
        ),
      },
    ],
    [channelFilters, unreadAtMount],
  );

  if (selectedRow) {
    //* 대상 수가 배포마다 달라, 탭 인덱스가 범위를 벗어나면 첫 탭으로 떨어뜨린다
    const tabIndex = channelIndex < selectedRow.notes.length ? channelIndex : 0;
    const note = selectedRow.notes[tabIndex];

    return (
      <div className="mx-auto max-w-screen-2xl flex-1 px-4 py-6 md:px-10 md:py-8">
        <div className="mb-4 flex items-center gap-3">
          <Button onClick={() => setSelectedReleaseId(null)}>← 목록</Button>
          <p className="text-sm font-bold">{selectedRow.releaseId} 업데이트</p>
        </div>

        {/* 읽는 화면이라 본문 폭은 따로 좁힌다 */}
        <div className="max-w-4xl space-y-3">
          <Tabs
            items={selectedRow.notes.map(
              (item) =>
                RELEASE_NOTE_CHANNEL_LABEL[item.channel as ReleaseNoteChannel] ??
                item.channel,
            )}
            selectedTab={tabIndex}
            setSelectedTab={setChannelIndex}
          />
          {note && (
            <div className="space-y-3 rounded p-6 theme-bg-1">
              <h2 className="text-lg font-bold">{note.title}</h2>
              <MiniMarkdown body={note.body} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-2xl flex-1 space-y-4 px-4 py-6 md:px-10 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs theme-text-8">
          배포마다 정리한 업데이트 안내입니다. 행을 누르면 대상별로 전문을 볼 수 있습니다.
        </p>
        <Input.Search
          allowClear
          className="w-72"
          placeholder="기능·내용 검색 (예: 발송취소, 메모)"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
      </div>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(row) => row.releaseId}
        size="middle"
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: '표시할 업데이트 소식이 없습니다.' }}
        onRow={(record) => ({
          className: 'cursor-pointer',
          onClick: () => {
            setSelectedReleaseId(record.releaseId);
            setChannelIndex(0);
          },
        })}
      />
    </div>
  );
};

export default AdminReleaseNotePage;
